// OfficePreviewModal — 메신저 docx·xlsx·pptx 첨부의 «미리보기» 버튼을 누르면 뜨는 게이트웨이 모달.
//
// 설계: `patch_mm_office_preview_p2_frontend.md` §3-4 (우산 `design_mm_office_preview.md`).
//
// 흐름: 브라우저가 **본인 MM 쿠키로** 파일을 받는다 → 게이트웨이 `POST /api/office-preview` 로 다시 올린다
//       → 돌아온 쪽 PNG 를 보이는 쪽부터 차례로 보여 준다. 메신저 권한은 건드리지 않는다(이미 받을 수 있는 파일만).
// 버튼은 MM iframe 안(utils/mmOfficePreview)에 넣고, 모달은 iframe 밖에서 우리가 그린다(ReadReceiptModal 선례).
//
// 🔴 officePreviewAPI 는 함수 안에서만 읽는다 — EmbedPage 시험이 client 를 mmAPI 만 가짜로 둔다.
// 🔴 쪽은 한 번에 1건씩 — `/api/` 초당 한도가 있고, blob 요청에는 img loading="lazy" 가 안 먹는다.
// 🔴 열리면 dialog 에 포커스를 둔다 — 버튼이 iframe 안이라 포커스가 iframe 문서에 남으면
//    최상위 window 의 keydown 이 안 불려 Escape 가 MM 미리보기 창만 닫는다.
import { useEffect, useRef, useState } from 'react';
import { officePreviewAPI } from '../api/client';
import { MAX_PREVIEW_BYTES, OFFICE_MESSAGES, officeErrorMessage } from '../utils/mmOfficeAttachment';
import useIsMobile from '../hooks/useIsMobile';

export const MAX_PREVIEW_PAGES = 30;   // 백엔드 P1 쪽 상한(truncated 일 때 보이는 쪽 수)
const MM_FILES = '/mm/api/v4/files/';

export const MODAL_TEXT = {
  loading: '미리보기를 만드는 중…',
  infoFailed: '파일 정보를 읽지 못했습니다',
  fileFailed: '파일을 받지 못했습니다',
  pageFailed: '이 쪽을 불러오지 못했습니다',
  truncated: `앞 ${MAX_PREVIEW_PAGES}쪽만 보여 줍니다 — 전체는 메신저에서 다운로드하세요`,
  xlsx: '엑셀은 인쇄 모양(쪽 나눔)으로 보입니다',
  empty: '보여 줄 쪽이 없습니다',
};

function extOf(name) {
  const s = String(name || '');
  const dot = s.lastIndexOf('.');
  return dot < 0 ? '' : s.slice(dot + 1).toLowerCase();
}

export default function OfficePreviewModal({ fileId, fileName, onClose }) {
  const isMobile = useIsMobile();
  const [phase, setPhase] = useState('loading');   // loading | ready | error
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState(null);    // {preview_id, page_count, truncated}
  const [urls, setUrls] = useState({});            // 쪽 번호 → objectURL
  const [pageErr, setPageErr] = useState({});      // 쪽 번호 → true(실패)
  const dialogRef = useRef(null);
  const listRef = useRef(null);
  const sessionRef = useRef(null);                 // 지금 파일 한 벌의 진행 상태(닫히면 alive=false)
  const closedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // 같은 Escape 가 dialog·window 양쪽에서 와도 onClose 는 한 번만.
  const requestClose = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    try { if (typeof onCloseRef.current === 'function') onCloseRef.current(); } catch (e) { /* */ }
  };

  // 보이는 쪽 중 가장 앞 쪽 하나를 받는다 — 끝나면 다음 쪽(한 번에 1건).
  const pump = (s) => {
    if (!s || !s.alive || s.busy || !s.previewId) return;
    let next = 0;
    s.wanted.forEach((n) => { if (!s.state[n] && (!next || n < next)) next = n; });
    if (!next) return;
    s.busy = true;
    s.state[next] = 'loading';
    s.wanted.delete(next);
    let req;
    try { req = Promise.resolve(officePreviewAPI.page(s.previewId, next)); } catch (e) { req = Promise.reject(e); }
    req.then((res) => {
      if (!s.alive) return;   // 닫힌 뒤 온 응답 — objectURL 을 만들지 않는다(만들면 아무도 해제하지 않는다)
      let url = null;
      try { url = URL.createObjectURL(res && res.data); } catch (e) { url = null; }
      if (!url) {
        s.state[next] = 'error';
        setPageErr((m) => ({ ...m, [next]: true }));
        return;
      }
      s.urls.push(url);
      s.state[next] = 'done';
      setUrls((m) => ({ ...m, [next]: url }));
    }, () => {
      if (!s.alive) return;
      s.state[next] = 'error';
      setPageErr((m) => ({ ...m, [next]: true }));
    }).then(() => {
      s.busy = false;
      pump(s);
    });
  };

  const retryPage = (n) => {
    const s = sessionRef.current;
    if (!s || !s.alive) return;
    delete s.state[n];
    s.wanted.add(n);
    setPageErr((m) => { const x = { ...m }; delete x[n]; return x; });
    pump(s);
  };

  // 열리면 dialog 에 포커스 + window Escape(포커스가 dialog 밖으로 나가도 닫히게).
  useEffect(() => {
    try { if (dialogRef.current) dialogRef.current.focus(); } catch (e) { /* */ }
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 파일 받기 → 올리기. 늦게 온 응답은 버린다(s.alive). 닫을 때 만든 objectURL 을 모두 해제한다.
  useEffect(() => {
    const s = { alive: true, busy: false, previewId: null, wanted: new Set(), state: {}, urls: [] };
    sessionRef.current = s;
    closedRef.current = false;
    setPhase('loading');
    setMessage('');
    setPreview(null);
    setUrls((m) => (Object.keys(m).length ? {} : m));
    setPageErr((m) => (Object.keys(m).length ? {} : m));
    const fail = (text) => { if (s.alive) { setMessage(text); setPhase('error'); } };
    const id = encodeURIComponent(String(fileId || ''));

    (async () => {
      let info = null;
      try {
        const r = await fetch(MM_FILES + id + '/info', { credentials: 'include' });
        if (!s.alive) return;
        if (!r || r.status !== 200) { fail(MODAL_TEXT.infoFailed); return; }
        info = await r.json();
      } catch (e) { fail(MODAL_TEXT.infoFailed); return; }
      if (!s.alive) return;
      const size = Number(info && info.size);
      if (Number.isFinite(size) && size > MAX_PREVIEW_BYTES) { fail(OFFICE_MESSAGES.too_large); return; }

      let blob = null;
      try {
        const r = await fetch(MM_FILES + id, { credentials: 'include' });
        if (!s.alive) return;
        if (!r || r.status !== 200) { fail(MODAL_TEXT.fileFailed); return; }
        blob = await r.blob();
      } catch (e) { fail(MODAL_TEXT.fileFailed); return; }
      if (!s.alive) return;

      try {
        const res = await officePreviewAPI.create(blob, fileName);
        if (!s.alive) return;
        const d = (res && res.data) || {};
        const count = Math.max(0, Math.floor(Number(d.page_count) || 0));
        s.previewId = d.preview_id || null;
        setPreview({ preview_id: d.preview_id, page_count: count, truncated: !!d.truncated });
        setPhase('ready');
      } catch (err) {
        const resp = err && err.response;
        fail(officeErrorMessage(resp && resp.status, resp && resp.data && resp.data.code));
      }
    })();

    return () => {
      s.alive = false;
      s.urls.forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* */ } });
      s.urls = [];
    };
  }, [fileId]);

  // 쪽 자리가 그려진 뒤 — 보이는 쪽만 받는다. IntersectionObserver 가 없으면 1쪽부터 차례로.
  useEffect(() => {
    const s = sessionRef.current;
    if (phase !== 'ready' || !preview || !preview.page_count || !s || !s.alive) return undefined;
    const all = () => {
      for (let n = 1; n <= preview.page_count; n += 1) s.wanted.add(n);
      pump(s);
    };
    const IO = typeof window !== 'undefined' ? window.IntersectionObserver : undefined;
    if (typeof IO !== 'function') { all(); return undefined; }
    let io = null;
    try {
      io = new IO((entries) => {
        if (!s.alive) return;
        (entries || []).forEach((en) => {
          const n = Number(en && en.target && en.target.getAttribute && en.target.getAttribute('data-page'));
          if (!n) return;
          if (en.isIntersecting) s.wanted.add(n); else s.wanted.delete(n);
        });
        pump(s);
      }, { root: listRef.current || null, rootMargin: '200px 0px' });
      const nodes = listRef.current ? listRef.current.querySelectorAll('[data-page]') : [];
      for (let i = 0; i < nodes.length; i += 1) io.observe(nodes[i]);
    } catch (e) {
      io = null;
      all();
    }
    return () => { try { if (io) io.disconnect(); } catch (e) { /* */ } };
  }, [phase, preview]);

  const isXlsx = extOf(fileName) === 'xlsx';
  const pages = [];
  if (phase === 'ready' && preview) {
    for (let n = 1; n <= preview.page_count; n += 1) pages.push(n);
  }

  const boxStyle = isMobile
    ? { width: '100%', height: '100%', maxWidth: 'none', maxHeight: 'none', borderRadius: 0 }
    : { width: '900px', maxWidth: '94vw', height: '88vh', borderRadius: '14px', boxShadow: '0 10px 40px rgba(0,0,0,.2)' };
  const noteStyle = { margin: '0 0 10px', padding: '8px 12px', fontSize: '12.5px', color: '#6b6a66', background: '#fff', border: '1px solid #eceae6', borderRadius: '8px' };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2147483600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : '20px', boxSizing: 'border-box' }}
    >
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={`미리보기 ${fileName || ''}`.trim()} tabIndex={-1}
        data-testid="gw-office-preview"
        onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); requestClose(); } }}
        style={{ ...boxStyle, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '12px 16px', borderBottom: '1px solid #eceae6', flexShrink: 0 }}>
          <b style={{ fontSize: '14.5px', color: '#1f1e1c', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={fileName}>{fileName}</b>
          <button type="button" onClick={requestClose} aria-label="닫기"
            style={{ background: 'none', border: 'none', fontSize: '20px', lineHeight: 1, color: '#8a8880', cursor: 'pointer', padding: '0 2px', flexShrink: 0 }}>×</button>
        </div>
        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', background: '#f3f2ef', padding: isMobile ? '10px' : '16px' }}>
          {phase === 'loading' && (
            <p role="status" style={{ margin: '40px 0', textAlign: 'center', fontSize: '13.5px', color: '#6b6a66' }}>{MODAL_TEXT.loading}</p>
          )}
          {phase === 'error' && (
            <p role="alert" style={{ margin: '40px 0', textAlign: 'center', fontSize: '13.5px', color: '#b4442a' }}>{message}</p>
          )}
          {phase === 'ready' && isXlsx && <p style={noteStyle}>{MODAL_TEXT.xlsx}</p>}
          {phase === 'ready' && preview && !preview.page_count && (
            <p style={{ margin: '40px 0', textAlign: 'center', fontSize: '13.5px', color: '#6b6a66' }}>{MODAL_TEXT.empty}</p>
          )}
          {pages.map((n) => (
            <div key={n} data-page={n}
              style={urls[n]
                ? { margin: '0 auto 12px', maxWidth: '100%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.12)' }
                : { margin: '0 auto 12px', width: '100%', aspectRatio: '1 / 1.414', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.12)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              {urls[n] ? (
                <img src={urls[n]} alt={`${n}쪽`} style={{ display: 'block', width: '100%', height: 'auto' }} />
              ) : pageErr[n] ? (
                <>
                  <span style={{ fontSize: '13px', color: '#b4442a' }}>{MODAL_TEXT.pageFailed}</span>
                  <button type="button" onClick={() => retryPage(n)}
                    style={{ padding: '5px 14px', fontSize: '12.5px', border: '1px solid #d1d5db', borderRadius: '7px', background: '#fff', color: '#374151', cursor: 'pointer' }}>다시</button>
                </>
              ) : (
                <span style={{ fontSize: '12.5px', color: '#a3a19b' }}>{n}쪽</span>
              )}
            </div>
          ))}
          {phase === 'ready' && preview && preview.truncated && <p style={noteStyle}>{MODAL_TEXT.truncated}</p>}
        </div>
      </div>
    </div>
  );
}
